import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING, EMZI_TYPOGRAPHY, getButtonStyle } from '../styles/emziCI';
import { createPackV3, PACK_TYPES } from '../api/knowledgePacksV3';
import { txt } from '../utils/bilingualText';

const InlinePackCreator = ({ isOpen, onClose, onPackCreated, relationshipType = 'requires' }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [quickPackData, setQuickPackData] = useState({
    pack_type: 'values',
    name_en: '',
    name_ms: '',
    description_en: '',
    description_ms: '',
    visibility: 'public',
  });

  const handleQuickCreate = async () => {
    // Validation
    if (!quickPackData.name_en.trim()) {
      toast.error('Name (English) is required');
      return;
    }
    if (!quickPackData.name_ms.trim()) {
      toast.error('Name (Malay) is required');
      return;
    }

    setIsCreating(true);
    try {
      // Build minimal pack payload
      const newPackPayload = {
        pack_type: quickPackData.pack_type,
        name: {
          en: quickPackData.name_en.trim(),
          ms: quickPackData.name_ms.trim(),
        },
        description: {
          en: quickPackData.description_en.trim() || 'Quick-created pack',
          ms: quickPackData.description_ms.trim() || 'Pek dicipta pantas',
        },
        visibility: quickPackData.visibility,
        metadata: {
          status: 'draft',
          created_via: 'inline_quick_add',
        },
        content: {}, // Empty content for now
        relationships: {
          parent_pack_id: null,
          child_pack_ids: [],
          linked_packs: [],
        },
        compliance_profile: {
          level: 'medium',
          restricted_phrases: [],
          safe_alternatives: {},
        },
        ai_context: {
          base_weight: 0.5,
          tone_influence: 0.5,
          applicable_soa_stages: [],
        },
      };

      const createdPack = await createPackV3(newPackPayload);

      // Prepare relationship object for parent pack
      const newRelationship = {
        pack_id: createdPack.pack_id,
        pack_name: createdPack.name,
        pack_type: createdPack.pack_type,
        relationship_type: relationshipType,
        linked_by: 'user',
        linked_at: new Date().toISOString(),
      };

      toast.success(`${txt(createdPack.name)} created and linked!`);
      onPackCreated(newRelationship);
      
      // Reset form
      setQuickPackData({
        pack_type: 'values',
        name_en: '',
        name_ms: '',
        description_en: '',
        description_ms: '',
        visibility: 'public',
      });
      onClose();
    } catch (error) {
      console.error('Quick create failed:', error);
      toast.error(error.message || 'Failed to create pack');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          backdropFilter: 'blur(2px)',
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: EMZI_COLORS.background,
          borderRadius: EMZI_RADIUS.lg,
          padding: EMZI_SPACING.lg,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          zIndex: 1001,
          width: '90%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: EMZI_SPACING.md,
          }}
        >
          <h3 style={{ ...EMZI_TYPOGRAPHY.h3, margin: 0 }}>Quick Create Pack</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: EMZI_COLORS.textSecondary,
              padding: 0,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <p style={{ fontSize: '12px', color: EMZI_COLORS.textSecondary, marginBottom: EMZI_SPACING.md }}>
          Create a new pack and automatically link it to your current pack.
        </p>

        {/* Pack Type */}
        <div style={{ marginBottom: EMZI_SPACING.md }}>
          <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
            Pack Type *
          </label>
          <select
            value={quickPackData.pack_type}
            onChange={(e) => setQuickPackData({ ...quickPackData, pack_type: e.target.value })}
            style={{
              width: '100%',
              minHeight: '40px',
              padding: '8px 12px',
              fontSize: '14px',
              border: `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.md,
              backgroundColor: EMZI_COLORS.background,
            }}
          >
            <option value="values">Values</option>
            <option value="compliance">Compliance</option>
            <option value="direction">Direction</option>
            <option value="product">Product</option>
            <option value="promotion">Promotion</option>
            <option value="services">Services</option>
            <option value="ambassador">Ambassador</option>
            <option value="health">Health</option>
            <option value="viral">Viral</option>
            <option value="trend">Trend</option>
          </select>
        </div>

        {/* Name (English) */}
        <div style={{ marginBottom: EMZI_SPACING.md }}>
          <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
            Name (English) *
          </label>
          <input
            type="text"
            value={quickPackData.name_en}
            onChange={(e) => setQuickPackData({ ...quickPackData, name_en: e.target.value })}
            placeholder="e.g., EMZI Brand Values"
            style={{
              width: '100%',
              minHeight: '40px',
              padding: '8px 12px',
              fontSize: '14px',
              border: `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.md,
              backgroundColor: EMZI_COLORS.background,
            }}
          />
        </div>

        {/* Name (Malay) */}
        <div style={{ marginBottom: EMZI_SPACING.md }}>
          <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
            Name (Malay) *
          </label>
          <input
            type="text"
            value={quickPackData.name_ms}
            onChange={(e) => setQuickPackData({ ...quickPackData, name_ms: e.target.value })}
            placeholder="e.g., Nilai Jenama EMZI"
            style={{
              width: '100%',
              minHeight: '40px',
              padding: '8px 12px',
              fontSize: '14px',
              border: `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.md,
              backgroundColor: EMZI_COLORS.background,
            }}
          />
        </div>

        {/* Description (English) */}
        <div style={{ marginBottom: EMZI_SPACING.md }}>
          <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
            Description (English)
          </label>
          <textarea
            value={quickPackData.description_en}
            onChange={(e) => setQuickPackData({ ...quickPackData, description_en: e.target.value })}
            placeholder="Brief description..."
            rows={2}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '14px',
              border: `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.md,
              backgroundColor: EMZI_COLORS.background,
              resize: 'vertical',
            }}
          />
        </div>

        {/* Description (Malay) */}
        <div style={{ marginBottom: EMZI_SPACING.md }}>
          <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
            Description (Malay)
          </label>
          <textarea
            value={quickPackData.description_ms}
            onChange={(e) => setQuickPackData({ ...quickPackData, description_ms: e.target.value })}
            placeholder="Penerangan ringkas..."
            rows={2}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '14px',
              border: `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.md,
              backgroundColor: EMZI_COLORS.background,
              resize: 'vertical',
            }}
          />
        </div>

        {/* Visibility */}
        <div style={{ marginBottom: EMZI_SPACING.lg }}>
          <label style={{ ...EMZI_TYPOGRAPHY.label, display: 'block', marginBottom: EMZI_SPACING.xs }}>
            Visibility
          </label>
          <select
            value={quickPackData.visibility}
            onChange={(e) => setQuickPackData({ ...quickPackData, visibility: e.target.value })}
            style={{
              width: '100%',
              minHeight: '40px',
              padding: '8px 12px',
              fontSize: '14px',
              border: `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.md,
              backgroundColor: EMZI_COLORS.background,
            }}
          >
            <option value="public">Public</option>
            <option value="group_restricted">Group Restricted</option>
            <option value="studio_restricted">Studio Restricted</option>
          </select>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: EMZI_SPACING.sm }}>
          <button
            onClick={onClose}
            disabled={isCreating}
            style={{
              ...getButtonStyle('outlined'),
              flex: 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleQuickCreate}
            disabled={isCreating}
            style={{
              ...getButtonStyle('primary'),
              flex: 1,
            }}
          >
            {isCreating ? 'Creating...' : `Create & Link (${relationshipType})`}
          </button>
        </div>
      </div>
    </>
  );
};

export default InlinePackCreator;
